import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../api/api';
import { toast } from '../hooks/use-toast';
import { Robot, ArrowLeft, Play, PaperPlaneTilt, X, Lightning, TrendUp, Warning, Target, ChatCircleDots } from '@phosphor-icons/react';

const fmtDate = (s) => (s ? new Date(s).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '—');

const BriefCard = ({ run }) => {
  if (!run?.brief) return null;
  const b = run.brief;
  const Section = ({ icon: Icon, title, items, color }) => (
    items?.length > 0 && (
      <div className="mt-4">
        <p className={`text-xs font-black uppercase flex items-center gap-1.5 ${color} mb-1.5`}><Icon size={14} weight="bold" /> {title}</p>
        <ul className="space-y-1">{items.map((it, i) => <li key={i} className="text-sm font-medium text-neutral-800 pl-4 border-l-2 border-neutral-300">{it}</li>)}</ul>
      </div>
    )
  );
  return (
    <div className="bg-white border-2 border-black p-6 shadow-[6px_6px_0px_#000]" data-testid="chief-brief-card">
      <p className="text-xs font-black uppercase text-neutral-400 mb-1">Morning Brief · {fmtDate(run.finished_at)} · {run.trigger}</p>
      <h2 className="text-xl font-black text-neutral-900 mb-2" data-testid="chief-brief-headline">{b.headline}</h2>
      <p className="text-sm text-neutral-700">{b.summary}</p>
      <Section icon={TrendUp} title="Wins" items={b.wins} color="text-emerald-700" />
      <Section icon={Warning} title="Concerns" items={b.concerns} color="text-amber-700" />
      <Section icon={Target} title="Recommendations" items={b.recommendations} color="text-[#D95800]" />
    </div>
  );
};

const DraftCard = ({ d, onSend, onDismiss, busy }) => {
  const [reply, setReply] = useState(d.draft_reply);
  return (
    <div className="bg-white border-2 border-black p-4" data-testid={`chief-draft-${d.id}`}>
      <div className="flex items-start justify-between gap-3 mb-2">
        <div>
          <p className="font-black text-sm">{d.name} <span className="text-neutral-400 font-bold">&lt;{d.email}&gt;</span></p>
          <p className="text-xs font-bold text-neutral-500">{d.subject} {d.topic && <span className="px-1.5 py-0.5 bg-blue-100 text-blue-800 uppercase text-[10px] font-black ml-1">{d.topic}</span>}</p>
        </div>
        <span className="text-[11px] font-bold text-neutral-400 shrink-0">{fmtDate(d.created_at)}</span>
      </div>
      <p className="text-xs text-neutral-600 bg-neutral-100 border border-neutral-200 p-2 mb-2 max-h-24 overflow-y-auto">{d.message}</p>
      <p className="text-[10px] font-black uppercase text-[#D95800] mb-1">AI-drafted reply — edit before sending</p>
      <textarea value={reply} onChange={(e) => setReply(e.target.value)} rows={4}
        className="w-full border-2 border-neutral-900 p-2 text-sm font-medium" data-testid={`chief-draft-text-${d.id}`} />
      <div className="flex gap-2 mt-2">
        <button onClick={() => onSend(d.id, reply)} disabled={busy}
          className="h-9 px-4 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-black uppercase border-2 border-black shadow-[2px_2px_0px_#000] flex items-center gap-1.5 disabled:opacity-50"
          data-testid={`chief-draft-send-${d.id}`}>
          <PaperPlaneTilt size={14} weight="bold" /> Approve & Send
        </button>
        <button onClick={() => onDismiss(d.id)} disabled={busy}
          className="h-9 px-3 bg-white text-neutral-600 text-xs font-black uppercase border-2 border-neutral-400 flex items-center gap-1"
          data-testid={`chief-draft-dismiss-${d.id}`}>
          <X size={14} weight="bold" /> Dismiss
        </button>
      </div>
    </div>
  );
};

const ChiefChat = () => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [directives, setDirectives] = useState([]);
  const bottomRef = React.useRef(null);

  const loadDirectives = useCallback(() => {
    axiosInstance.get('/chief/directives').then((r) => setDirectives(r.data.directives || [])).catch(() => {});
  }, []);

  useEffect(() => {
    axiosInstance.get('/chief/chat/history').then((r) => setMessages(r.data.messages || [])).catch(() => {});
    loadDirectives();
  }, [loadDirectives]);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const send = async () => {
    const text = input.trim();
    if (!text || sending) return;
    setInput('');
    setSending(true);
    setMessages((m) => [...m, { role: 'user', content: text }]);
    try {
      const r = await axiosInstance.post('/chief/chat', { message: text });
      setMessages((m) => [...m, { role: 'assistant', content: r.data.reply, directive: r.data.directive }]);
      if (r.data.directive) loadDirectives();
    } catch (e) {
      setMessages((m) => [...m, { role: 'assistant', content: e.response?.data?.detail || 'Something went wrong — try again.' }]);
    } finally { setSending(false); }
  };

  const closeDirective = async (id) => {
    try {
      await axiosInstance.post(`/chief/directives/${id}/close`);
      setDirectives((d) => d.filter((x) => x.id !== id));
    } catch (e) { /* noop */ }
  };

  return (
    <div className="bg-white border-2 border-black shadow-[6px_6px_0px_#000]" data-testid="chief-chat-panel">
      <div className="bg-neutral-900 text-white px-4 py-3 flex items-center gap-2">
        <ChatCircleDots size={20} weight="fill" className="text-[#D95800]" />
        <p className="font-black uppercase text-sm">Ask your Chief of Staff</p>
        <span className="text-[10px] font-bold text-neutral-400 ml-auto">Live data · standing orders · can run outreach searches</span>
      </div>
      {directives.length > 0 && (
        <div className="px-4 pt-3 flex flex-wrap gap-2" data-testid="chief-directives-list">
          {directives.map((d) => (
            <span key={d.id} className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-orange-50 border-2 border-[#D95800] text-[11px] font-bold text-neutral-800" data-testid={`chief-directive-${d.id}`}>
              📌 {d.text}
              <button onClick={() => closeDirective(d.id)} className="text-neutral-400 hover:text-red-600" data-testid={`chief-directive-close-${d.id}`}>
                <X size={12} weight="bold" />
              </button>
            </span>
          ))}
        </div>
      )}
      <div className="h-72 overflow-y-auto p-4 space-y-3" data-testid="chief-chat-messages">
        {messages.length === 0 && (
          <p className="text-sm text-neutral-400 font-medium">Try: "How did ads do this month?" · "Find roofing suppliers in Dallas to pitch ad space" · "Every week, search for plumbers in Austin" · "Next run, prioritize selling roofing slots"</p>
        )}
        {messages.map((m, i) => (
          <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-[80%] px-3 py-2 text-sm font-medium border-2 whitespace-pre-line ${m.role === 'user' ? 'bg-[#D95800] text-white border-black' : 'bg-neutral-100 text-neutral-900 border-neutral-300'}`}>
              {m.content}
              {m.directive && (
                <p className="mt-1.5 text-[11px] font-black text-[#D95800] bg-white border border-[#D95800] px-1.5 py-0.5">📌 Standing order saved: {m.directive}</p>
              )}
            </div>
          </div>
        ))}
        {sending && <p className="text-xs font-bold text-neutral-400 animate-pulse">Chief is thinking…</p>}
        <div ref={bottomRef} />
      </div>
      <div className="border-t-2 border-black p-3 flex gap-2">
        <input value={input} onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && send()}
          placeholder="Ask a question or give an instruction…"
          className="flex-1 border-2 border-neutral-900 px-3 py-2 text-sm font-medium"
          data-testid="chief-chat-input" />
        <button onClick={send} disabled={sending || !input.trim()}
          className="h-10 px-4 bg-[#D95800] text-white font-black uppercase text-xs border-2 border-black shadow-[2px_2px_0px_#000] disabled:opacity-50 flex items-center gap-1.5"
          data-testid="chief-chat-send">
          <PaperPlaneTilt size={14} weight="bold" /> Send
        </button>
      </div>
    </div>
  );
};

const SOURCE_LABELS = {
  chief_chat: { label: 'Chief Chat', cls: 'bg-orange-100 text-orange-800' },
  campaign: { label: 'Auto-Pilot', cls: 'bg-blue-100 text-blue-800' },
  manual_find: { label: 'Manual Find', cls: 'bg-neutral-200 text-neutral-700' },
  lead_hunt: { label: 'Lead Hunt', cls: 'bg-emerald-100 text-emerald-800' },
};

const SearchLogTable = ({ logs }) => {
  if (!logs?.length) return null;
  return (
    <div data-testid="chief-search-log">
      <h2 className="text-sm font-black uppercase text-neutral-500 mb-3">🔎 Search execution history</h2>
      <div className="bg-white border-2 border-black overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-neutral-900 text-white text-left">
              <th className="px-3 py-2 text-xs font-black uppercase">When</th>
              <th className="px-3 py-2 text-xs font-black uppercase">Source</th>
              <th className="px-3 py-2 text-xs font-black uppercase">Search</th>
              <th className="px-3 py-2 text-xs font-black uppercase text-right">Found</th>
              <th className="px-3 py-2 text-xs font-black uppercase text-right">Imported</th>
              <th className="px-3 py-2 text-xs font-black uppercase text-right">Errors</th>
            </tr>
          </thead>
          <tbody>
            {logs.map((l, i) => {
              const s = SOURCE_LABELS[l.source] || { label: l.source, cls: 'bg-neutral-200 text-neutral-700' };
              return (
                <tr key={i} className="border-t border-neutral-200 hover:bg-orange-50" data-testid={`search-log-row-${i}`}>
                  <td className="px-3 py-2 font-bold text-neutral-500 whitespace-nowrap text-xs">{fmtDate(l.at)}</td>
                  <td className="px-3 py-2"><span className={`px-2 py-0.5 text-[10px] font-black uppercase rounded ${s.cls}`}>{s.label}</span></td>
                  <td className="px-3 py-2 font-bold">
                    {l.trade}{l.target ? ` · ${l.target}` : ''}{l.kind && l.kind !== 'homeowner_leads' ? <span className="text-neutral-400 font-medium"> ({l.kind === 'ad_sales' ? 'ad sales' : 'recruiting'})</span> : ''}
                    {l.error_detail && (
                      <p className="text-[11px] font-medium text-red-600 mt-0.5" data-testid={`search-log-error-${i}`}>⚠ {l.error_detail}</p>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right font-black">{l.found}</td>
                  <td className={`px-3 py-2 text-right font-black ${l.imported > 0 ? 'text-emerald-700' : 'text-neutral-400'}`}>{l.imported}</td>
                  <td className={`px-3 py-2 text-right font-black ${l.errors > 0 ? 'text-red-600' : 'text-neutral-300'}`}>{l.errors}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const AdminChief = () => {
  const navigate = useNavigate();
  const [runs, setRuns] = useState(null);
  const [drafts, setDrafts] = useState([]);
  const [searchLogs, setSearchLogs] = useState([]);
  const [running, setRunning] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [r1, r2, r3] = await Promise.all([
        axiosInstance.get('/chief/runs'),
        axiosInstance.get('/chief/drafts'),
        axiosInstance.get('/chief/search-logs'),
      ]);
      setRuns(r1.data.runs || []);
      setDrafts(r2.data.drafts || []);
      setSearchLogs(r3.data.logs || []);
      if ((r1.data.runs || [])[0]?.status === 'running') setTimeout(load, 5000);
      else setRunning(false);
    } catch (e) {
      if (e.response?.status === 403) navigate('/admin');
      else setRuns([]);
    }
  }, [navigate]);

  useEffect(() => { load(); }, [load]);

  const runNow = async () => {
    setRunning(true);
    try {
      await axiosInstance.post('/chief/run');
      toast({ title: 'Agent running', description: 'Scanning the business — the brief lands here and in your inbox in ~1 minute.' });
      setTimeout(load, 6000);
    } catch (e) {
      setRunning(false);
      toast({ title: 'Could not start', description: e.response?.data?.detail || 'Try again', variant: 'destructive' });
    }
  };

  const sendDraft = async (id, reply) => {
    setBusy(true);
    try {
      await axiosInstance.post(`/chief/drafts/${id}/send`, { reply });
      toast({ title: 'Reply sent' });
      setDrafts((d) => d.filter((x) => x.id !== id));
    } catch (e) {
      toast({ title: 'Send failed', description: e.response?.data?.detail || 'Try again', variant: 'destructive' });
    } finally { setBusy(false); }
  };

  const dismissDraft = async (id) => {
    setBusy(true);
    try {
      await axiosInstance.post(`/chief/drafts/${id}/dismiss`);
      setDrafts((d) => d.filter((x) => x.id !== id));
    } catch (e) { /* noop */ } finally { setBusy(false); }
  };

  const latest = runs?.find((r) => r.status === 'complete');
  const isRunning = running || runs?.[0]?.status === 'running';

  return (
    <div className="min-h-screen bg-[#E8E9EB] py-8 px-4" data-testid="admin-chief-page">
      <div className="max-w-5xl mx-auto space-y-6">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/admin')} className="w-10 h-10 bg-white border-2 border-black flex items-center justify-center shadow-[3px_3px_0px_#000]" data-testid="chief-back-btn">
              <ArrowLeft size={18} weight="bold" />
            </button>
            <div>
              <h1 className="text-2xl font-black uppercase flex items-center gap-2"><Robot size={28} weight="fill" className="text-[#D95800]" /> AI Chief of Staff</h1>
              <p className="text-xs font-bold text-neutral-500">Runs every morning · drafts only, you approve everything</p>
            </div>
          </div>
          <button onClick={runNow} disabled={isRunning}
            className="h-12 px-6 bg-[#D95800] hover:bg-[#E55D00] text-white font-black uppercase border-2 border-black shadow-[4px_4px_0px_#000] flex items-center gap-2 disabled:opacity-60"
            data-testid="chief-run-now-btn">
            {isRunning ? <><Lightning size={18} weight="fill" className="animate-pulse" /> Running…</> : <><Play size={18} weight="fill" /> Run Now</>}
          </button>
        </div>

        {runs === null ? (
          <p className="text-center text-neutral-400 font-bold py-10">Loading…</p>
        ) : (
          <>
            {latest ? <BriefCard run={latest} /> : (
              <div className="bg-white border-2 border-black p-8 text-center" data-testid="chief-empty-state">
                <p className="font-black text-lg mb-1">No runs yet</p>
                <p className="text-sm text-neutral-500">Hit "Run Now" — the agent scans signups, jobs, ads, leads and messages, takes safe draft actions, and writes your first brief.</p>
              </div>
            )}

            <ChiefChat />

            <SearchLogTable logs={searchLogs} />

            {drafts.length > 0 && (
              <div data-testid="chief-drafts-section">
                <h2 className="text-sm font-black uppercase text-neutral-500 mb-3">📝 Draft replies awaiting your approval ({drafts.length})</h2>
                <div className="space-y-3">
                  {drafts.map((d) => <DraftCard key={d.id} d={d} onSend={sendDraft} onDismiss={dismissDraft} busy={busy} />)}
                </div>
              </div>
            )}

            {runs.length > 0 && (
              <div data-testid="chief-activity-feed">
                <h2 className="text-sm font-black uppercase text-neutral-500 mb-3">Agent activity</h2>
                <div className="space-y-3">
                  {runs.slice(0, 6).map((r) => (
                    <div key={r.id} className="bg-white border-2 border-neutral-900 p-4" data-testid={`chief-run-${r.id}`}>
                      <div className="flex items-center justify-between flex-wrap gap-2">
                        <p className="font-black text-sm">
                          {r.status === 'complete' ? '✅' : r.status === 'running' ? '⏳' : '❌'} {fmtDate(r.started_at)}
                          <span className="text-neutral-400 font-bold ml-2 uppercase text-xs">{r.trigger}</span>
                        </p>
                        <p className="text-xs font-bold text-neutral-500">{r.drafts_created} drafts · {r.prospects_promoted} outreach adds</p>
                      </div>
                      {r.activity?.length > 0 && (
                        <ul className="mt-2 space-y-1">
                          {r.activity.map((a, i) => (
                            <li key={i} className="text-xs font-medium text-neutral-700 pl-3 border-l-2 border-[#D95800]">{a.detail}</li>
                          ))}
                        </ul>
                      )}
                      {r.error && <p className="text-xs font-bold text-red-600 mt-2">{r.error}</p>}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default AdminChief;
